import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { NewEvaluationService } from './new-evaluation.service';
import { SentryService } from './sentry.service';

const CHUNK_LOAD_PATTERNS: string[] = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'is not a valid JavaScript MIME type',
];

const RELOAD_GUARD_KEY = 'pwa_recovery_last_reload';
const RELOAD_GUARD_WINDOW_MS = 60_000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private readonly injector: Injector,
    private readonly sentryService: SentryService,
  ) {}

  handleError(error: any): void {
    const message = this.extractMessage(error);

    if (!this.isChunkLoadFailure(message)) {
      this.sentryService.captureException(error);
      return;
    }

    this.sentryService.captureException(error, {
      tags: { feature: 'pwa-recovery' },
      extra: { errorType: 'chunk_load_failure', errorMessage: message },
    });

    if (this.isReloadThrottled()) {
      return;
    }

    this.markReload();
    this.recover();
  }

  private extractMessage(error: any): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  private isChunkLoadFailure(message: string): boolean {
    return CHUNK_LOAD_PATTERNS.some((pattern) => message.includes(pattern));
  }

  private isReloadThrottled(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      return Date.now() - last < RELOAD_GUARD_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private markReload(): void {
    try {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // sessionStorage no disponible — se ignora
    }
  }

  private recover(): void {
    const newEvaluationService = this.injector.get(NewEvaluationService);
    const alertController = this.injector.get(AlertController);

    Promise.resolve(newEvaluationService.forcePersist())
      .catch(() => undefined)
      .then(() =>
        alertController.create({
          header: 'Recargando aplicación',
          message:
            'Se detectó un error en la caché. La aplicación se está recargando...',
          backdropDismiss: false,
        }),
      )
      .then((alert) => alert.present())
      .finally(() => {
        setTimeout(() => document.location.reload(), 1000);
      });
  }
}
